import { useState } from "react";
import { X, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const TYPES = [
  { v: "enhancer_owned", label: "My own space (90/10 finder's fee)" },
  { v: "partner", label: "Partner venue (custom split)" },
  { v: "online", label: "Online (Zoom/virtual)" },
  { v: "mobile", label: "Mobile / off-site" },
];
const PRIVACY = [
  { v: "public", label: "Show full public address" },
  { v: "city_only", label: "Show city & state only" },
  { v: "after_booking", label: "Reveal after booking" },
  { v: "after_payment", label: "Reveal after payment" },
  { v: "after_agreement", label: "Reveal after agreement signed" },
  { v: "admin_only", label: "Private (admin only)" },
];

export default function LocationFormModal({ location, onClose, onSaved }) {
  const editing = !!location?.id;
  const [form, setForm] = useState({
    name: location?.name || "", type: location?.type || "enhancer_owned",
    address: location?.address || "", city: location?.city || "", state: location?.state || "",
    zip: location?.zip || "", privacy: location?.privacy || "after_booking",
    capacity: location?.capacity || 0, description: location?.description || "",
    parking: location?.parking || "", accessibility: location?.accessibility || "", contact: location?.contact || "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const payload = { ...form, capacity: parseInt(form.capacity) || 0 };
    try {
      if (editing) await api.put(`/enhancer/locations/${location.id}`, payload);
      else await api.post("/enhancer/locations", payload);
      toast.success(editing ? "Location updated." : "Location added — pending admin approval.");
      onSaved();
      onClose();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] grid place-items-center px-4 bg-black/40 backdrop-blur-sm overflow-y-auto py-10" data-testid="location-form-modal">
      <div className="relative w-full max-w-lg bg-[#F9F7F3] rounded-3xl shadow-2xl p-8 my-auto">
        <button onClick={onClose} className="absolute top-4 right-4 text-[#4A5D6B] hover:text-[#144F75]"><X size={20} /></button>
        <h3 className="font-serif text-2xl text-[#1A242B] mb-1">{editing ? "Edit location" : "Add a location"}</h3>
        <p className="text-sm text-[#4A5D6B] mb-5">Revenue model is set by location type. Your own space uses a 10% finder's fee.</p>
        <form onSubmit={submit} className="space-y-3">
          <input required placeholder="Location name" value={form.name} onChange={set("name")} data-testid="loc-name" className="eyw-input w-full" />
          <select value={form.type} onChange={set("type")} data-testid="loc-type" className="eyw-input w-full">
            {TYPES.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
          </select>
          <input placeholder="Street address" value={form.address} onChange={set("address")} className="eyw-input w-full" />
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <input placeholder="City" value={form.city} onChange={set("city")} data-testid="loc-city" className="eyw-input w-full" />
            <input placeholder="State" value={form.state} onChange={set("state")} className="eyw-input w-full" />
            <input placeholder="Zip" value={form.zip} onChange={set("zip")} className="eyw-input w-full" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <select value={form.privacy} onChange={set("privacy")} data-testid="loc-privacy" className="eyw-input w-full">
              {PRIVACY.map((p) => <option key={p.v} value={p.v}>{p.label}</option>)}
            </select>
            <input type="number" placeholder="Capacity" value={form.capacity} onChange={set("capacity")} className="eyw-input w-full" />
          </div>
          <textarea placeholder="Description / room notes" rows={2} value={form.description} onChange={set("description")} className="eyw-input w-full resize-none" />
          {error && <p className="text-sm text-red-600">{error}</p>}
          <button type="submit" disabled={loading} data-testid="loc-save" className="eyw-btn w-full disabled:opacity-60">
            {loading && <Loader2 className="animate-spin" size={18} />} {editing ? "Save changes" : "Add location"}
          </button>
        </form>
      </div>
    </div>
  );
}
